import { Box, Paper, Grid, Chip, Stack, Card, CardContent } from "@mui/material";
import {
  AccessTime as ClockIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { Text, InternalLink } from "@src/lib/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Shift } from "@src/interface";
import { ShiftChipBar } from "@src/appV2/Calendar/ShiftChips/ShiftChipBar";
import { formatPhone } from "src/utils/strings";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { getWorkerShiftTypeLabel } from "@src/appV2/redesign/utils/worker";
import { parseISO } from "date-fns";
import { roundDecimal, roundUpAndFormatRateWithZeroes } from "@src/utils/numbers";
import { formatDateTime, formatTime } from "@clipboard-health/date-time";
import { useShiftChips } from "@src/appV2/Calendar/ShiftChips/useShiftChips";

export function ShiftSummary({ shift }: { shift: Shift }) {
  const shiftChips = useShiftChips({
    shift,
  });

  return (
    <Paper sx={{ marginBottom: 2, overflow: "hidden" }}>
      <Box sx={{ padding: "12px 16px", borderBottom: "1px solid #e0e0e0", bgcolor: "#e3f2fd" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Text variant="subtitle1" bold>
                Shift #{shift._id}
              </Text>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Text variant="body2" color="secondary">
                {shift.facility?.name} · {shift.facility?.fullAddress?.state}
              </Text>
              <LocationIcon fontSize="small" sx={{ color: "#757575" }} />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {shift.clockInOut?.end && (
              <Chip
                icon={<CheckIcon sx={{ fontSize: 16 }} />}
                label="Completed"
                size="small"
                sx={{
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                  "& .MuiChip-icon": { color: "#2e7d32" },
                }}
              />
            )}
            {!shift.clockInOut?.end && isDefined(shift.agentId) && (
              <Chip
                icon={<ClockIcon sx={{ fontSize: 16 }} />}
                label="In Progress"
                size="small"
                sx={{
                  bgcolor: "#fff3e0",
                  color: "#ed6c02",
                  "& .MuiChip-icon": { color: "#ed6c02" },
                }}
              />
            )}
            {!isDefined(shift.agentId) && (
              <Chip
                label="Open"
                size="small"
                sx={{
                  bgcolor: "#ffebee",
                  color: "#d32f2f",
                }}
              />
            )}
            <Chip
              label={shift.agentReq}
              size="small"
              sx={{
                bgcolor: "#e3f2fd",
                color: "#1976d2",
              }}
            />
            {shift.verified && (
              <Chip
                icon={<CheckIcon sx={{ fontSize: 16 }} />}
                label="Verified"
                size="small"
                sx={{
                  bgcolor: "#e8f5e9",
                  color: "#2e7d32",
                  "& .MuiChip-icon": { color: "#2e7d32" },
                }}
              />
            )}
          </Stack>
        </Box>

        <Box sx={{ mt: 1 }}>
          <ShiftChipBar shiftChips={shiftChips} />
        </Box>
      </Box>

      <Box sx={{ padding: "12px 16px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Text variant="body2" color="secondary" bold>
              Date & Time
            </Text>
            <Text variant="body2" sx={{ mt: 0.5 }}>
              {formatDateTime(parseISO(shift.start))} - {formatTime(shift.end)}
            </Text>
            <Text variant="body2" color="secondary">
              Duration: {shift.time?.toFixed(2)} hours
            </Text>
          </Grid>
          <Grid item xs={12} md={4}>
            <Text variant="body2" color="secondary" bold>
              Agent
            </Text>
            {shift.agent ? (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  {shift.agentId ? (
                    <InternalLink
                      to={getLocation("workplaceWorkerDetails", {
                        pathParams: { workerId: shift.agentId },
                      })}
                      underline="hover"
                    >
                      {shift.agent.name}
                    </InternalLink>
                  ) : (
                    <Text variant="body2">{shift.agent.name}</Text>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Text variant="body2" color="secondary">
                    {getWorkerShiftTypeLabel(shift.agentReq, shift.facility?.fullAddress?.state)}
                  </Text>
                  <Text variant="body2" color="secondary">
                    {shift.selfClaim ? "(Self-claimed)" : "(Assigned)"}
                  </Text>
                </Stack>
                {shift.agent.phone && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon fontSize="small" sx={{ color: "#757575" }} />
                    <Text variant="body2" color="secondary">
                      {formatPhone(shift.agent.phone)}
                    </Text>
                  </Stack>
                )}
              </>
            ) : (
              <Text variant="body2" sx={{ mt: 0.5, color: "#d32f2f" }}>
                No agent assigned
              </Text>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Text variant="body2" color="secondary" bold>
              Payment Details
            </Text>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              <Text variant="body2">Pay: {roundUpAndFormatRateWithZeroes(shift.pay ?? 0)}</Text>
              <Text variant="body2">Charge: {roundDecimal(shift.charge ?? 0)}</Text>
              {shift.shiftBlockId && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Text variant="body2">Block ID: {shift.shiftBlockId}</Text>
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Posted by information */}
        {shift.postedBy && (
          <Box sx={{ mt: 2 }}>
            <Text variant="body2" color="secondary">
              Posted By: {shift.postedBy.name}
              {shift.postedBy.userType === "EMPLOYEE" ? " (Clipboard Admin)" : ""}
            </Text>
          </Box>
        )}

        {/* Notes section */}
        {shift.note && (
          <Card sx={{ mt: 2, bgcolor: "#fafafa" }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <DescriptionIcon fontSize="small" sx={{ color: "#757575" }} />
                <Text variant="body2" bold>
                  Shift Notes
                </Text>
              </Stack>
              <Box sx={{ pl: 4 }}>
                {shift.note && (
                  <Text variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {shift.note}
                  </Text>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Paper>
  );
}
